import { createSelector, createSlice } from '@reduxjs/toolkit';
import { data, key, validate } from './user-form.utils';
import type { Data, KeyValue, PayloadValidation, PayloadValidationData, UserFormType } from './user-form.types';

const initialState = {
  form: {},
  touch: {},
  validation: {
    success: false,
    hasErrors: false,
    suspicious: [],
    output: {},
    fields: {},
  },
  isDirty: false,
  isInitialized: false,
} satisfies UserFormType as UserFormType;

const UserFormSlice = createSlice({
  name: 'user-form',
  initialState,
  reducers: (create) => ({
    setUserFormKeys: create.reducer<UserFormType['form']>((state, action) => {
      state.form = action.payload;
    }),
    setUserFormKey: create.reducer<PayloadValidationData<KeyValue>>((state, action) => {
      state.form[action.payload.data.key] = action.payload.data.value;
      state.validation = validate({
        schema: action.payload.schema,
        layout: action.payload.layout,
        form: state.form,
        touch: state.touch,
        isDirty: state.isDirty,
      });
    }),
    /**
     * Writes several keys at once and revalidates. Used when the suspicious
     * fields dialog is confirmed: until then its inputs live outside the store.
     */
    setUserFormValues: create.reducer<PayloadValidationData<Data<string>>>((state, action) => {
      Object.assign(state.form, action.payload.data);
      state.validation = validate({
        schema: action.payload.schema,
        layout: action.payload.layout,
        form: state.form,
        touch: state.touch,
        isDirty: state.isDirty,
      });
    }),
    setTouchKey: create.reducer<PayloadValidationData<string>>((state, action) => {
      state.touch[action.payload.data] = true;
      state.validation = validate({
        schema: action.payload.schema,
        layout: action.payload.layout,
        form: state.form,
        touch: state.touch,
        isDirty: state.isDirty,
      });
    }),
    setIsDirty: create.reducer<PayloadValidation>((state, action) => {
      state.isDirty = true;
      state.validation = validate({
        schema: action.payload.schema,
        layout: action.payload.layout,
        form: state.form,
        touch: state.touch,
        isDirty: state.isDirty,
      });
    }),
    setInitialize: create.reducer<PayloadValidation>((state, action) => {
      const dataForm = data({
        layout: action.payload.layout,
        value: '',
        useIsDefault: true,
      });
      const dataTouch = data({
        layout: action.payload.layout,
        value: false,
      });
      state.isInitialized = true;
      state.form = dataForm;
      state.touch = dataTouch;
      state.validation = validate({
        schema: action.payload.schema,
        layout: action.payload.layout,
        form: state.form,
        touch: state.touch,
        isDirty: state.isDirty,
      });
    }),
    toInitialState: create.reducer(() => initialState),
  }),
  selectors: {
    userForm: (userForm) => userForm,
    form: (userForm) => userForm.form,
    touch: (userForm) => userForm.touch,
    validation: (userForm) => userForm.validation,
    isDirty: (userForm) => userForm.isDirty,
    isInitialized: (userForm) => userForm.isInitialized,
    agb: (userForm) => key({ form: userForm.form, key: 'agb', fb: '' }),
    firstName: (userForm) => key({ form: userForm.form, key: 'first_name', fb: '' }),
    lastName: (userForm) => key({ form: userForm.form, key: 'last_name', fb: '' }),
    email: (userForm) => key({ form: userForm.form, key: 'email', fb: '' }),
    phone: (userForm) => key({ form: userForm.form, key: 'phone', fb: '' }),
    fullName: (userForm) => {
      const firstName = key({ form: userForm.form, key: 'first_name', fb: '' });
      const lastName = key({ form: userForm.form, key: 'last_name', fb: '' });
      return [firstName, lastName].join(' ');
    },
    dataForContact: createSelector(
      [
        (userForm) => key({ form: userForm.form, key: 'first_name', fb: '' }),
        (userForm) => key({ form: userForm.form, key: 'last_name', fb: '' }),
        (userForm) => key({ form: userForm.form, key: 'email', fb: '' }),
        (userForm) => key({ form: userForm.form, key: 'phone', fb: '' }),
      ],
      (firstName, lastName, email, phone) => ({
        name: [firstName, lastName].join(' '),
        email,
        phone,
      }),
    ),
  },
});

export default UserFormSlice;
